import { Box, Button, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SaveIcon from '@mui/icons-material/Save';
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MyListApi } from "../utils/restApi";
import { setUserStatus } from "../slices/itemsSlice";
import { TagChooser } from "./TagChooser";
import { DatePicker } from "@mui/x-date-pickers";
import CancelIcon from '@mui/icons-material/Cancel';
import { ConfirmDialog } from "./ConfirmDialog";
import { notification } from "../utils/utils";
import { LocationProperty } from "./LocationProperty";
import { LinkProperty } from "./LinkProperty";
import { ArrayProperty } from "./ArrayProperty";
import { LinkDialog } from "./LinkDialog";
import { ImageProperty } from "./ImageProperty";
import { EnumProperty } from "./property-updates/EnumProperty";
import { ProgressCover } from "./ProgressCover";
import { EnumArrayField } from "./items/EnumArrayField";

const getDefaultVal = type => {
    if (type === 'string' || type === 'enum') return '';
    return null;
}

export const ItemCreate = props => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const user = useSelector(state => state.login.user);
    const [searchParams] = useSearchParams();

    const initialSelectedTag = searchParams.get('tag');

    const [properties, setProperties] = useState({});
    const [selectedTag, setSelectedTag] = useState(initialSelectedTag);
    const [itemLinks, setItemLinks] = useState({});
    const [creating, setCreating] = useState(false);
    const [name, setName] = useState('');
    const ref = useRef();
    const linkDialogRef = useRef({});

    // if (initialSelectedTag) {
    //     setSelectedTag(initialSelectedTag);
    // }

    // useEffect(() => {
    //     setSelectedTag(initialSelectedTag);
    //     handleTagSelect(initialSelectedTag);
    // }, [initialSelectedTag]);

    const handleSubmit = e => {
        e.preventDefault();
        e.target.blur();

        ref.current.openConfirm(() => {
            setCreating(true);
            const propCount = selectedTag?.data_info.length || 0;

            const data = {};
            for (let ix = 0; ix < propCount; ix++) {
                const name = selectedTag?.data_info[`${ix}`].name;
                const type = selectedTag?.data_info[`${ix}`].type;
                const p = ((type, ix, properties) => {
                    let val = null;
                    if (type === 'date') {
                        val = properties[`${ix}`] && properties[`${ix}`].toISOString();
                    }
                    val = properties[`${ix}`];
                    return val;
                })(type, ix, properties);
                data[name] = p;
            }

            const temp = Object.fromEntries(
                Object.entries(itemLinks).map(([key, value]) => [key, value.map(v => v.id)])
            );

            MyListApi.createItem2(selectedTag.id, name, data, temp)
                .then(data => {
                    setCreating(false);
                    dispatch(setUserStatus('INITIAL'));
                    // navigate(-1);
                    navigate(`/items/${data.id}`);
                })
                .catch(error => {
                    setCreating(false);
                    notification(error.message, "error");
                });
        });
    }

    const handleTagSelect = val => {
        setSelectedTag(val);
        const count = val?.data_info.length || 0;
        const newProperties = {};
        const newItemLinks = {};
        for (let ix = 0; ix < count; ix++) {
            const di = val?.data_info[ix];
            const type = di.type;
            if (type === 'item_link') {
                newItemLinks[`${di.name}`] = [];
            } else {
                newProperties[`${ix}`] = getDefaultVal(type);
            }
        }
        setItemLinks(newItemLinks);
        setProperties(newProperties);
    }

    const handleChange = index => {
        const type = selectedTag?.data_info[index].type;
        if (type == 'string' || type == 'enum' || type == "integer" || type === "array_enum") {
            return (e) => {
                const val = e.target.value;
                setProperties({ ...properties, [`${index}`]: val})
            }
        }
        return (e) => {
            setProperties({ ...properties, [`${index}`]: e})
        }
    }

    const canSave = () => {
        if (!selectedTag) {
            return false;
        }
        if (!name || name.length < 1) {
            return false;
        }
        return true;
    }

    const onItemLinkChange = key => (item, index) => {
        const items = [...itemLinks[key]];
        if (index != null) {
            items[index] = item;
        } else {
            items.push(item);
        }
        setItemLinks({ ...itemLinks, [key]: items });
    }

    const onItemLinkRemove = (key, index) => () => {
        const items = [...itemLinks[key]];
        items.splice(index, 1);
        setItemLinks({ ...itemLinks, [key]: items });
    }

    const onEditLink = ix => item => {
        linkDialogRef.current[item.tag.id]?.editDialog(item, ix);
    }

    return (
        <Box sx={{ width: '100%', }}>
            <ConfirmDialog
                title="Item Creation"
                message="New items are in a pending status until approved by an Admin or Moderator."
                // showCancel={false}
                ref={ref}
            />
            <ProgressCover open={creating} label="Creating Item..." />
            <form onSubmit={handleSubmit}>
                <Paper sx={{ display: 'flex', flexDirection: 'column', rowGap: '20px', padding: '20px'}}>
                    {/* <Box>
                        <Typography variant="h4">Create Stuff</Typography>
                    </Box> */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <TagChooser sx={{ width: '100%' }} curTag={initialSelectedTag} onSelect={handleTagSelect} />
                        <TextField id="name" label="Name" source="name" variant="filled" size="small" required={true} value={name} onChange={e => setName(e.target.value)} disabled={!selectedTag} />
                    </Box>
                    {selectedTag ?
                    <>
                    <Box>
                        <Typography variant="h6">Item Details</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {(selectedTag?.data_info || []).map((p, index) => {
                                    const id = `prop-id-${index}`;
                                    const label = p.name;
                                    const meta = p?.meta || {};
                                    const options = p?.options || {};
                                    let inputElement = <Box sx={{ width: '100%' }}></Box>;

                                    switch (p.type) {
                                        case 'array_string':
                                            inputElement = <ArrayProperty label={label} onChange={handleChange(index)} inputElement={<TextField />} />;
                                            break;
                                        case 'location':
                                            inputElement = <LocationProperty id={id} onChange={handleChange(index)} value={properties[`${index}`]} />;
                                            break;
                                        case 'string':
                                            inputElement = <TextField variant='filled' label={label} id={id} onChange={handleChange(index)} value={properties[`${index}`]} sx={{ width: '100%' }} />;
                                            break;
                                        case 'integer':
                                            inputElement = <TextField variant="filled" label={label} id={id} onChange={handleChange(index)} value={properties[`${index}`]} sx={{ width: '100%' }} />;
                                            break;
                                        case 'date':
                                            inputElement =
                                                <DatePicker
                                                    slotProps={{ textField: { variant: 'filled' }}}
                                                    label={label}
                                                    id={id}
                                                    onChange={handleChange(index)}
                                                    value={properties[`${index}`]}
                                                    sx={{ width: '100%' }}
                                                    slots={{
                                                        actionBar: ({ onClear, onAccept }) => (
                                                          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px" }}>
                                                            <Button onClick={() => setProperties({ ...properties, [`${index}`]: null })} color="secondary">
                                                              Clear
                                                            </Button>
                                                          </div>
                                                        ),
                                                    }}
                                                />;
                                            break;
                                        case 'enum':
                                            inputElement = <EnumProperty label={label} value={properties[`${index}`]} onChange={handleChange(index)} dataInfo={p} />
                                            break;
                                        case 'item_link':
                                            // inputElement = <TextField variant='filled' label={label} id={id} onChange={handleChange(index)} value={properties[`${index}`]} sx={{ width: '100%' }} />;
                                            // inputElement = <LinkProperty value={properties[`${index}`]} label={label} tagId={meta.tags} onChange={handleChange(index)} />;
                                            break;
                                        case 'image':
                                            inputElement = <ImageProperty label={label} onChange={handleChange(index)}/>
                                            break;
                                        case 'array_enum':
                                            inputElement = <EnumArrayField label={label} value={properties[`${index}`] || []} onChange={handleChange(index)} dataInfo={p} /> //<div>enum array</div>
                                            break;
                                    }
                                    return (
                                        <Box key={index} sx={{ width: '100%' }}>
                                            {inputElement}
                                        </Box>
                                    )
                                })
                            }
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="h6">Item Links</Typography>
                    </Box>
                    <Box>
                        {Object.keys(itemLinks).map((k, keyIx) => {
                            const di = selectedTag.data_info.filter(f => f.name === k)[0];
                            const items = itemLinks[k];
                            return (
                                <div key={keyIx}>
                                    <b>{k}</b>
                                    {items.map((i, ix) => {
                                        return <LinkProperty key={ix} item={i} onEdit={onEditLink(ix)} onRemove={onItemLinkRemove(k, ix)} />
                                    })}
                                    {/* <LinkProperty key={keyIx} label={k} tagId={di.meta.tag} value={null} onChange={onItemLinkChange(k)} /> */}
                                    <LinkDialog ref={el => linkDialogRef.current[di.meta.tag] = el} label={k} tagId={di.meta.tag} onChange={onItemLinkChange(k)} onEdit={onEditLink} />
                                </div>
                            )
                        })}
                    </Box>
                    </>
                    : null}
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px', width: '100%', margin: '10px 0 0 0', justifyContent: 'right' }}>
                        <Button startIcon={<SaveIcon />} variant="contained" type="submit" disabled={!canSave()}>Save</Button>
                    </Box>
                </Paper>
            </form>
        </Box>
    );
}